/**
 * svelte-i18n bootstrap.
 *
 * Strategy: register all supported locales with lazy loaders (Vite splits
 * each lang JSON into its own chunk), init with the browser-detected locale,
 * fall back to English.
 *
 * Initial locale resolution order:
 *   1. `?lang=xx` query param (when present in OAuth handshake URL)
 *   2. `navigator.language` (mapped to a supported short code)
 *   3. 'en' fallback
 */
import { register, init, locale, getLocaleFromNavigator, _ } from 'svelte-i18n';

export const SUPPORTED_LANGS = ['en', 'bg', 'de', 'es', 'fr', 'it'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

const FALLBACK_LANG: SupportedLang = 'en';

export const LANG_LABELS: Record<SupportedLang, string> = {
	en: 'English',
	bg: 'Български',
	de: 'Deutsch',
	es: 'Español',
	fr: 'Français',
	it: 'Italiano',
};

export function setupI18n(): void {
	for (const lang of SUPPORTED_LANGS) {
		register(lang, () => import(`../lang/${lang}.json`));
	}

	init({
		fallbackLocale: FALLBACK_LANG,
		initialLocale: resolveInitialLocale(),
	});
}

export function setLocale(lang: SupportedLang): void {
	locale.set(lang);
	if (typeof document !== 'undefined') {
		document.documentElement.lang = lang;
	}
}

function resolveInitialLocale(): string {
	if (typeof window !== 'undefined') {
		const fromQuery = new URLSearchParams(window.location.search).get('lang');
		if (fromQuery && isSupported(fromQuery)) return fromQuery;
	}
	const navLang = (getLocaleFromNavigator() || '').slice(0, 2).toLowerCase();
	if (isSupported(navLang)) return navLang;
	return FALLBACK_LANG;
}

function isSupported(lang: string): lang is SupportedLang {
	return (SUPPORTED_LANGS as readonly string[]).includes(lang);
}

export { _ };
